import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Routes } from '@/routes';
import { useDevice } from '@/hooks';
import { Flex, PageContainer, LoadingSpin, PageError } from '@/components';
import {
  fetchMyStudentPreRegistrations,
  fetchStudentPreRegistrationSchedule,
  getActiveStudentPreRegistrationSchedule,
} from '@/services/dormitoryStudent.service';
import { dormitoryStudentQueryKeys } from '../dormitoryStudentQueryKeys';
import { PreRegistrationStatus } from '@/configs';
import { parseError } from '@/utils';
import { DormitoryPreRegisterForm } from './DormitoryPreRegisterForm';
import { DormitoryPreRegistrationResult } from './DormitoryPreRegistrationResult';

export function DormitoryPreRegister() {
  const navigate = useNavigate();
  const { isDesktop } = useDevice();
  const [t] = useTranslation(['common', 'dormitory']);

  const scheduleQuery = useQuery({
    queryKey: dormitoryStudentQueryKeys.studentPreRegistrationSchedule(),
    queryFn: fetchStudentPreRegistrationSchedule,
    retry: false,
  });

  const myPreRegsQuery = useQuery({
    queryKey: dormitoryStudentQueryKeys.myPreRegistrations(),
    queryFn: fetchMyStudentPreRegistrations,
    retry: false,
  });

  const loading = scheduleQuery.isPending || myPreRegsQuery.isPending;

  const activeSchedule = useMemo(
    () => getActiveStudentPreRegistrationSchedule(scheduleQuery.data ?? null),
    [scheduleQuery.data],
  );

  /** Ignore deleted/expired rows — same as legacy pre-register (no EXPIRE state). */
  const myPreRegistrations = useMemo(
    () => (myPreRegsQuery.data ?? []).filter(
      (r) =>
        r.preRegistrationStatus !== PreRegistrationStatus.Delete
        && r.preRegistrationStatus !== PreRegistrationStatus.Expire,
    ),
    [myPreRegsQuery.data],
  );

  const existingForCurrent = useMemo(() => {
    if (activeSchedule == null) {
      return undefined;
    }
    return myPreRegistrations.find(
      (r) => r.preRegistrationEventId === activeSchedule.id,
    );
  }, [activeSchedule, myPreRegistrations]);

  const backText = t('common:labels.back');
  const pageTitle = t('dormitory:labels.pre-register');

  if (loading) {
    return (
      <Flex align="center" className="h-full pb-20">
        <LoadingSpin />
      </Flex>
    );
  }

  if (scheduleQuery.isError) {
    return (
      <PageError
        title={pageTitle}
        content={String(parseError(scheduleQuery.error, ''))}
        actionText={backText}
        action={() => navigate(Routes.Dormitory)}
      />
    );
  }

  if (myPreRegsQuery.isError) {
    return (
      <PageError
        title={pageTitle}
        content={String(parseError(myPreRegsQuery.error, ''))}
        action={myPreRegsQuery.refetch}
      />
    );
  }

  const schedule = scheduleQuery.data;
  if (schedule == null) {
    return (
      <PageError
        title={pageTitle}
        content={t('dormitory:errors.pre-register-not-active-for-you')}
        actionText={backText}
        action={() => navigate(Routes.Dormitory)}
      />
    );
  }

  if (activeSchedule == null) {
    return (
      <PageError
        title={pageTitle}
        content={t('dormitory:errors.not-in-pre-registeration-period')}
        actionText={backText}
        action={() => navigate(Routes.Dormitory)}
      />
    );
  }

  return (
    <PageContainer title={pageTitle}>
      <ConfigProvider componentSize={isDesktop() ? 'large' : 'middle'}>
        <Flex direction="column" align="center" className="h-full w-full pb-20">
          <div className="mx-auto w-full max-w-3xl px-4 pt-4">
            {existingForCurrent ? (
              <DormitoryPreRegistrationResult
                record={existingForCurrent}
                event={activeSchedule}
              />
            ) : (
              <DormitoryPreRegisterForm event={activeSchedule} />
            )}
          </div>
        </Flex>
      </ConfigProvider>
    </PageContainer>
  );
}
